import { css } from '@emotion/css';
import walletApi from 'apis/walletApi';
import classNames from 'classnames';
import globalStyle from 'constants/global.style';
import { colors } from 'constants/theme';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { NumericFormat } from 'react-number-format';
import depositPageStyle from './deposit.styles';
import TagLabel from 'components/tag-label/TagLabel';

interface UsdtProvider {
    provider_id: number;
    provider_code: string;
    provider_name: string;
    display_name: string;
    min_deposit: number;
    max_deposit: number;
    min_withdraw: number;
    max_withdraw: number;
    supported_networks: string;
    supported_coins: string;
    description: string;
    priority: number;
    withdraw_fee: number;
}

interface UsdtRate {
    provider_code: string;
    coin_symbol: string;
    rate_in: number;
    rate_out: number;
    rate: number;
    from_currency: string;
    to_currency: string;
}

interface DepositResult {
    transactionId: string;
    referenceId: string;
    address: string;
    amount: string;
    coinSymbol: string;
    network: string;
    qrImageUrl: string;
    expiresAt: string;
    fiatAmount: number;
    exchangeRate: number;
}

interface UserWallet {
    user_wallet_id: number;
    loginname: string;
    provider_id: number;
    provider_code: string;
    network_code: string;
    coin_symbol: string;
    address: string;
    balance: number;
    status: string;
}

const listAmountQuickUSDT = [
    { value: 10, label: '10$' },
    { value: 50, label: '50$' },
    { value: 100, label: '100$' },
    { value: 200, label: '200$' },
    { value: 500, label: '500$' },
    { value: 1000, label: '1000$' },
    { value: 2000, label: '2000$' },
    { value: 5000, label: '5000$' },
];

function UsdtDeposit() {
    const [loading, setLoading] = useState(false);
    const [creatingWallet, setCreatingWallet] = useState(false);
    const [rate, setRate] = useState<UsdtRate | null>(null);
    const [provider, setProvider] = useState<UsdtProvider | null>(null);
    const [wallet, setWallet] = useState<UserWallet | null>(null);
    const [hasCheckedWallet, setHasCheckedWallet] = useState(false);
    const [amount, setAmount] = useState('');
    const [depositResult, setDepositResult] = useState<DepositResult | null>(null);
    const [countdown, setCountdown] = useState<string>('');

    useEffect(() => {
        initUsdtDeposit();
    }, []);

    // Countdown timer for deposit expiration
    useEffect(() => {
        if (!depositResult?.expiresAt) {
            setCountdown('');
            return;
        }

        const calculateTimeLeft = () => {
            const expiresAt = new Date(depositResult.expiresAt).getTime();
            const now = Date.now();
            const diff = expiresAt - now;

            if (diff <= 0) {
                setCountdown('Đã hết hạn');
                return;
            }

            const minutes = Math.floor(diff / 60000);
            const seconds = Math.floor((diff % 60000) / 1000);
            setCountdown(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        };

        calculateTimeLeft();
        const timer = setInterval(calculateTimeLeft, 1000);

        return () => clearInterval(timer);
    }, [depositResult?.expiresAt]);

    const initUsdtDeposit = async () => {
        setLoading(true);
        try {
            const [providerRes, rateRes, walletRes] = await Promise.all([
                walletApi.usdtGetProviders(),
                walletApi.usdtGetRate({ provider_code: 'MFCPAY', coin_symbol: 'USDTTRC20' }),
                walletApi.usdtGetWallet({ provider_code: 'MFCPAY', coinSymbol: 'USDTTRC20' })
            ]);

            if (!providerRes.data.msg && providerRes.data.data?.length > 0) {
                setProvider(providerRes.data.data[0]);
            }

            if (!rateRes.data.msg && rateRes.data.data) {
                setRate(rateRes.data.data);
            }

            if (!walletRes.data.msg && walletRes.data.data?.length > 0) {
                setWallet(walletRes.data.data[0]);
            }
            setHasCheckedWallet(true);
        } catch (error) {
            console.log(error);
            toast.error('Không thể tải thông tin nạp USDT');
        } finally {
            setLoading(false);
        }
    };

    const createWallet = async () => {
        setCreatingWallet(true);
        try {
            const { data } = await walletApi.usdtCreateWallet({ coinSymbol: 'USDTTRC20' });
            if (!data.msg && data.data) {
                const walletRes = await walletApi.usdtGetWallet({ provider_code: 'MFCPAY', coinSymbol: 'USDTTRC20' });
                if (!walletRes.data.msg && walletRes.data.data?.length > 0) {
                    setWallet(walletRes.data.data[0]);
                }
                toast.success('Tạo ví USDT thành công!');
            } else {
                toast.error(data.msg || 'Không thể tạo ví');
            }
        } catch (error) {
            console.log(error);
            toast.error('Có lỗi xảy ra khi tạo ví');
        } finally {
            setCreatingWallet(false);
        }
    };

    const changeAmount = (value: string) => {
        const raw = value.replace(/[,]/g, '');
        if (raw === '') {
            setAmount('');
            return;
        }
        if (isNaN(Number(raw))) return;
        setAmount(raw);
    };

    const selectQuickAmount = (value: number) => {
        setAmount(String(value));
    };

    const submitDeposit = async () => {
        const amountValue = Number(amount);
        if (!amount || amountValue <= 0) {
            toast.error('Vui lòng nhập số USDT muốn nạp');
            return;
        }

        if (provider && provider.min_deposit > 0 && amountValue < provider.min_deposit) {
            toast.error(`Số USDT nạp tối thiểu ${provider.min_deposit}`);
            return;
        }

        if (provider && provider.max_deposit > 0 && amountValue > provider.max_deposit) {
            toast.error(`Số USDT nạp tối đa ${provider.max_deposit}`);
            return;
        }

        setLoading(true);
        try {
            const fiatAmount = (rate?.rate_in || 0) * amountValue;
            const { data } = await walletApi.usdtDeposit({
                coinSymbol: 'USDTTRC20',
                fiat_amount: String(fiatAmount)
            });

            if (!data.msg && data.data) {
                setDepositResult(data.data);
                toast.success('Tạo lệnh nạp tiền thành công!');
            } else {
                toast.error(data.msg || 'Có lỗi xảy ra');
            }
        } catch (error) {
            console.log(error);
            toast.error('Có lỗi xảy ra khi tạo lệnh nạp tiền');
        } finally {
            setLoading(false);
        }
    };

    const copyText = (text: string) => {
        navigator.clipboard.writeText(text);
        toast.success('Sao chép thành công!');
    };

    const resetDeposit = () => {
        setDepositResult(null);
        setAmount('');
    };

    if (loading && !rate) {
        return <div className={styles.loading}>Đang tải...</div>;
    }

    // Hiển thị màn hình tạo ví nếu chưa có ví
    if (hasCheckedWallet && !wallet) {
        return (
            <div className={styles.container}>
                <div className={styles.noWalletBox}>
                    <div className={styles.walletIcon}>💰</div>
                    <h6>Bạn chưa có ví USDT</h6>
                    <p>Để nạp tiền bằng USDT, bạn cần tạo ví trước.</p>
                    <button
                        type="button"
                        className={styles.createWalletBtn}
                        onClick={createWallet}
                        disabled={creatingWallet}
                    >
                        {creatingWallet ? 'Đang tạo ví...' : 'Tạo ví USDT'}
                    </button>
                </div>

                <div className={styles.guideSection}>
                    <h6>Thông tin về ví USDT</h6>
                    <ol>
                        <li>Ví USDT sẽ được tạo trên mạng TRON (TRC20)</li>
                        <li>Mỗi tài khoản chỉ có 1 ví USDT</li>
                        <li>Sau khi tạo ví, bạn có thể nạp tiền ngay lập tức</li>
                        <li>Ví được lưu vĩnh viễn, không cần tạo lại</li>
                    </ol>
                </div>
            </div>
        );
    }

    // Render deposit result
    if (depositResult) {
        return (
            <div className={styles.container}>
                <div className={styles.modalContainer}>
                    <div className={styles.modalHeader}>
                        <h6 className={styles.modalTitle}>Nạp USDT</h6>
                        <button type="button" className={styles.modalCloseBtn} onClick={resetDeposit}>✕</button>
                    </div>

                    <div className={styles.modalBody}>
                        {depositResult.expiresAt && (
                            <div className={styles.countdownBox}>
                                <span>Thời gian còn lại:</span>
                                <span className={styles.countdownValue}>{countdown}</span>
                            </div>
                        )}

                        <div className={styles.mainInfo}>
                            <div className={styles.qrBox}>
                                {depositResult.qrImageUrl && (
                                    <img src={depositResult.qrImageUrl} alt="QR" className={styles.qrImg} />
                                )}
                            </div>
                            <div className={styles.amountBox}>
                                <div className={styles.addressHeader}>
                                    <span>Địa chỉ ví nạp</span>
                                    <button type="button" className={styles.copyBtn} onClick={() => copyText(depositResult.address)}>
                                        Sao chép
                                    </button>
                                </div>
                                <p className={styles.addressValue}>{depositResult.address}</p>

                                <div className={styles.addressHeader} style={{ marginTop: 12 }}>
                                    <span>Số tiền (USDT)</span>
                                    <button type="button" className={styles.copyBtn} onClick={() => copyText(depositResult.amount)}>
                                        Sao chép
                                    </button>
                                </div>
                                <p className={styles.addressValue}>{depositResult.amount}</p>
                            </div>
                        </div>
                        {/* Lưu ý quan trọng */}
                        <div className={styles.warningBox}>
                            <h6>LƯU Ý QUAN TRỌNG</h6>
                            <ul>
                                <li>Vui lòng chỉ gửi <strong>USDT</strong> trên mạng lưới <strong>TRON (TRC20)</strong></li>
                                <li>Phí giao dịch không bao gồm trong số tiền hiển thị. Vui lòng cộng thêm <strong>phí gas</strong> vào số tiền cần chuyển (khoảng <strong>1-2$</strong>)</li>
                                <li>Đảm bảo chúng tôi sẽ nhận đúng số lượng TRC20-USDT là <strong>{depositResult.amount}</strong></li>
                                <li>Giao dịch có thể mất đến <strong>10 phút</strong> để hoàn tất trong khi chờ xác nhận từ blockchain</li>
                                <li>Chúng tôi <strong>không chịu trách nhiệm</strong> nếu bạn gửi tiền đến sai địa chỉ hoặc mạng lưới</li>
                            </ul>
                        </div>
                    </div>

                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.closeBtn} onClick={resetDeposit}>Tạo lệnh mới</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {provider && (
                <div className={styles.rateInfo}>
                    <span>Nạp tiền qua mạng lưới: </span>
                    <span className={styles.rateValue}>{provider.supported_networks}</span>
                </div>
            )}

            {/* Quick amounts */}
            <h6 className={depositPageStyle.titleBankGroup}>Chọn số USDT nạp nhanh</h6>
            <div className={styles.amountList}>
                {listAmountQuickUSDT.map((item) => (
                    <div className={styles.amountItem} key={item.value}>
                        <p
                            className={classNames(styles.amountItemInner, {
                                active: amount === String(item.value)
                            })}
                            onClick={() => selectQuickAmount(item.value)}
                        >
                            {item.label}
                        </p>
                    </div>
                ))}
            </div>

            {/* Input */}
            <h6 className={depositPageStyle.titleBankGroup}>Hoặc nhập số USDT</h6>
            <div className={styles.inputSection}>
                <NumericFormat
                    className="input-common"
                    decimalScale={6}
                    placeholder="Nhập số USDT (VD: 10)..."
                    value={amount}
                    onChange={(e) => changeAmount(e.target.value)}
                />
                {provider && (
                    <TagLabel
                        className={styles.minAmountTag}
                        label={`Nạp tối thiểu ${provider.min_deposit} USDT - tối đa ${new Intl.NumberFormat().format(provider.max_deposit || 0)} USDT`}
                    />
                )}
            </div>

            {/* Note */}
            <div className={styles.guideSection}>
                <h6>LƯU Ý</h6>
                <ol>
                    <li>1 USDT ~ <strong>{new Intl.NumberFormat().format(rate?.rate_in || 0)} VNĐ</strong></li>
                    <li>Nạp tối thiểu <strong>{provider?.min_deposit || 0}</strong> USDT</li>
                    <li>Nạp tối đa <strong>{new Intl.NumberFormat().format(provider?.max_deposit || 0)}</strong> USDT</li>
                </ol>
            </div>

            {/* Submit */}
            <div className={styles.submitSection}>
                <button
                    type="button"
                    className={globalStyle.btnPrimary}
                    onClick={submitDeposit}
                    disabled={loading}
                >
                    {loading ? 'Đang xử lý...' : 'TIẾP TỤC'}
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: css`
        margin-top: 12px;
        position: relative;
    `,
    loading: css`
        text-align: center;
        padding: 40px;
        color: ${colors.primary};
    `,
    rateInfo: css`
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        padding: 12px 16px;
        border-radius: 8px;
        margin-bottom: 16px;
        display: flex;
        align-items: center;
        gap: 8px;
        border: 1px solid #00d4aa;
    `,
    rateValue: css`
        color: #00d4aa;
        font-weight: bold;
        font-size: 16px;
    `,
    amountList: css`
        display: flex;
        flex-wrap: wrap;
        background: linear-gradient(to bottom, #B80005, #710003);
        border-radius: 8px;
        padding: 4px;
        margin-bottom: 12px;
    `,
    amountItem: css`
        padding: 3px;
        flex: 0 0 12.5%;
        max-width: 12.5%;
    `,
    amountItemInner: css`
        padding: 6px 0;
        border-radius: 8px;
        box-shadow: 1px 1px 2px rgba(0, 0, 0, .4);
        cursor: pointer;
        text-align: center;
        background: rgba(0, 0, 0, .4);
        color: var(--white-color);
        font-weight: 700;
        &:active, &:hover, &.active {
            background: linear-gradient(0deg, #002287 0%, rgba(2, 126, 251, 1) 100%);
        }
    `,
    inputSection: css`
        max-width: 400px;
        margin-bottom: 16px;
    `,
    minAmountTag: css`
        p {
            color: #ff0707 !important;
        }
    `,
    submitSection: css`
        max-width: 400px;
        margin-top: 20px;
    `,
    guideSection: css`
        margin-top: 24px;
        padding: 16px;
        background: rgba(0, 0, 0, 1);
        border-radius: 8px;
        h6 {
            color: #ffc107;
            margin-bottom: 12px;
        }
        ol {
            padding-left: 20px;
            li {
                margin-bottom: 8px;
                color: #fff;
            }
        }
    `,
    noWalletBox: css`
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        border: 1px solid #ffc107;
        border-radius: 12px;
        padding: 40px;
        text-align: center;
        margin-bottom: 24px;
        h6 {
            color: #ffc107;
            font-size: 20px;
            margin-bottom: 12px;
        }
        p {
            color: #ccc;
            margin-bottom: 24px;
        }
    `,
    walletIcon: css`
        font-size: 48px;
        margin-bottom: 16px;
    `,
    createWalletBtn: css`
        background: linear-gradient(135deg, #00d4aa 0%, #00a884 100%);
        border: none;
        color: #000;
        padding: 14px 32px;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 700;
        font-size: 16px;
        transition: all 0.3s ease;
        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px rgba(0, 212, 170, 0.4);
        }
        &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
        }
    `,
    // Deposit result styles
    modalContainer: css`
        background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
        border-radius: 16px;
        width: 100%;
        border: 1px solid #00d4aa;
    `,
    modalHeader: css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 12px 24px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    `,
    modalTitle: css`
        color: #00d4aa;
        font-size: 20px;
        font-weight: 600;
        margin: 0;
    `,
    modalCloseBtn: css`
        background: transparent;
        border: none;
        color: #888;
        font-size: 24px;
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 4px;
        &:hover {
            color: #fff;
        }
    `,
    modalBody: css`
        padding: 20px 24px;
    `,
    modalFooter: css`
        padding: 16px 24px 24px;
        display: flex;
        justify-content: center;
    `,
    countdownBox: css`
        background: linear-gradient(135deg, rgba(255, 193, 7, 0.15) 0%, rgba(255, 107, 107, 0.15) 100%);
        border: 1px solid #ffc107;
        border-radius: 8px;
        padding: 12px 16px;
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 12px;
        margin-bottom: 20px;
        color: #ffc107;
        font-size: 15px;
    `,
    countdownValue: css`
        font-weight: 700;
        font-size: 18px;
        color: #ff6b6b;
    `,
    mainInfo: css`
        display: flex;
        gap: 20px;
        margin-bottom: 20px;
        @media (max-width: 500px) {
            flex-direction: column;
            align-items: center;
        }
    `,
    qrBox: css`
        flex: 0 0 160px;
        background: #fff;
        padding: 8px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
    `,
    qrImg: css`
        width: 140px;
        height: 140px;
    `,
    amountBox: css`
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
    `,
    addressHeader: css`
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 4px;
        color: #888;
        font-size: 13px;
    `,
    addressValue: css`
        font-family: monospace;
        font-size: 15px;
        word-break: break-all;
        color: #fff;
        font-weight: 600;
        line-height: 1.5;
    `,
    copyBtn: css`
        background: ${colors.lottery};
        border: none;
        color: white;
        padding: 4px 12px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        &:hover {
            opacity: 0.8;
        }
    `,
    closeBtn: css`
        background: transparent;
        border: 1px solid #888;
        color: #888;
        padding: 10px 32px;
        border-radius: 8px;
        cursor: pointer;
        font-weight: 600;
        &:hover {
            border-color: #fff;
            color: #fff;
        }
    `,
    warningBox: css`
        background: rgba(255, 107, 107, 0.1);
        border: 1px solid #ff6b6b;
        border-radius: 8px;
        padding: 14px 16px;
        margin-top: 16px;
        h6 {
            color: #ff6b6b;
            margin-bottom: 10px;
            font-size: 14px;
        }
        ul {
            padding-left: 18px;
            margin: 0;
            li {
                color: #ccc;
                margin-bottom: 6px;
                font-size: 13px;
                line-height: 1.4;
                strong {
                    color: #ff6b6b;
                }
            }
        }
    `,
};

export default UsdtDeposit;
